import React, { useMemo } from 'react';
import { Card } from '../../../../../../../../CommonComponents/OPL/Card/Card';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { MarginProps } from '../../../../../../../../theme/styledProps';
import { MarketScenarioLabel, YearlyResult } from '../../../../tabs/MonteCarlo/MonteCarlo.meta';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import {
  ClickableTableRow,
  HtmlTable,
} from '../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { WorkingStatus } from './ResultsTable.meta';
import { Lozenge } from '../../../../../../../../CommonComponents/OPL/Lozenge/Lozenge';
import { useTheme } from '../../../../../../../../theme/theme';

const getAgeStatusHeader = (
  principalName: string | undefined,
  spouseName: string | null | undefined,
  hasSpouse: boolean,
): string => {
  if (!principalName) {
    return 'Age / Status';
  }
  if (hasSpouse && spouseName) {
    return `Ages: ${principalName}, ${spouseName}`;
  }
  return `Ages: ${principalName}`;
};

const COLUMN_SERIES: { key: keyof YearlyResult; header: string; series: string }[] = [
  {
    key: 'significantlyBelowAverage',
    header: 'Sig. Below Avg',
    series: MarketScenarioLabel.SignificantlyBelowAverage,
  },
  { key: 'belowAverage', header: 'Below Avg', series: MarketScenarioLabel.BelowAverage },
  { key: 'average', header: 'Average', series: MarketScenarioLabel.Average },
  { key: 'aboveAverage', header: 'Above Avg', series: MarketScenarioLabel.AboveAverage },
];

interface Props extends MarginProps {
  results: YearlyResult[];
  retirementAge?: number | null;
  spouseRetirementAge?: number | null;
  hasSpouse?: boolean;
  principalName?: string;
  spouseName?: string | null;
  hiddenSeries?: Set<string>;
}

export const ResultsTable = ({
  results,
  retirementAge,
  spouseRetirementAge,
  hasSpouse = false,
  principalName,
  spouseName,
  hiddenSeries,
  ...rest
}: Props) => {
  const theme = useTheme();
  const filteredResults = useMemo(
    () => results.filter(row => row.principalAge != null || row.spouseAge != null),
    [results],
  );

  const ageStatusHeader = getAgeStatusHeader(principalName, spouseName, hasSpouse);

  const getPrincipalStatus = (principalAge: number): WorkingStatus | null => {
    if (retirementAge == null) {
      return WorkingStatus.Working;
    }
    return principalAge > retirementAge ? WorkingStatus.Retired : WorkingStatus.Working;
  };

  const getSpouseStatus = (spouseAge: number): WorkingStatus | null => {
    if (spouseRetirementAge == null) {
      return WorkingStatus.Working;
    }
    return spouseAge > spouseRetirementAge ? WorkingStatus.Retired : WorkingStatus.Working;
  };

  return (
    <Box {...rest}>
      <Card p="24" hasBorder>
        <Text variant="cardTitle" mb="16">
          Year-by-Year Results
        </Text>
        <Text fontSize="body" color="neutral.500" mb="24">
          Projected portfolio values under each market condition scenario from the current year
          through the end of the plan.
        </Text>
        <HtmlTable variant="bordered" maxHeight={600} width="100%" fixedHead>
          <table>
            <colgroup>
              <col />
              <col width="20%" />
              {COLUMN_SERIES.map(c => (
                <col key={c.key} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>
                  <Text variant="tableHeader" textAlign="left">
                    Year
                  </Text>
                </th>
                <th style={{ zIndex: 2 }}>
                  <Text variant="tableHeader" textAlign="left">
                    {ageStatusHeader}
                  </Text>
                </th>
                {COLUMN_SERIES.map(col => (
                  <th key={col.key}>
                    <Text
                      variant="tableHeader"
                      textAlign="right"
                      style={{
                        color: hiddenSeries?.has(col.series)
                          ? theme.colors.neutral['300']
                          : theme.colors.neutral['700'],
                      }}
                    >
                      {col.header}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredResults.map(row => {
                const { principalAge, spouseAge } = row;
                const principalStatus = principalAge ? getPrincipalStatus(principalAge) : null;
                const spouseStatus = spouseAge ? getSpouseStatus(spouseAge) : null;
                return (
                  <ClickableTableRow key={row.year} variant="clickable">
                    <td>
                      <Text textAlign="left">{row.year}</Text>
                    </td>
                    <td>
                      <Box display="flex">
                        {principalAge !== null ? (
                          <Box
                            display="flex"
                            css={({ theme }) => ({ gap: theme.space['4'] })}
                            alignItems="center"
                            width="90px"
                          >
                            <Text textAlign="right" fontSize="small" fontWeight={500}>
                              {principalAge}{' '}
                            </Text>
                            {principalStatus !== null && (
                              <Lozenge
                                style={{ zIndex: 1 }}
                                variantColor={
                                  principalStatus === WorkingStatus.Working ? 'primary' : 'default'
                                }
                              >
                                {principalStatus}
                              </Lozenge>
                            )}
                          </Box>
                        ) : (
                          <Box display="flex" justifyContent="center" width="90px">
                            <Text textAlign="center" fontSize="small" fontWeight={500}>
                              -
                            </Text>
                          </Box>
                        )}

                        {spouseAge !== null ? (
                          <Box
                            display="flex"
                            css={({ theme }) => ({ gap: theme.space['4'] })}
                            width="90px"
                          >
                            <Text>{spouseAge && principalAge !== null ? ',' : ''}</Text>
                            <Box
                              display="flex"
                              css={({ theme }) => ({ gap: theme.space['4'] })}
                              alignItems="center"
                            >
                              <Text textAlign="right" fontSize="small" fontWeight={500}>
                                {spouseAge}{' '}
                              </Text>
                              {spouseStatus !== null && (
                                <Lozenge
                                  style={{ zIndex: 1 }}
                                  variantColor={
                                    spouseStatus === WorkingStatus.Working ? 'primary' : 'default'
                                  }
                                >
                                  {spouseStatus}
                                </Lozenge>
                              )}
                            </Box>
                          </Box>
                        ) : hasSpouse ? (
                          <Box display="flex" justifyContent="center" width="90px">
                            <Text textAlign="center" fontSize="small" fontWeight={500}>
                              -
                            </Text>
                          </Box>
                        ) : null}
                      </Box>
                    </td>
                    {COLUMN_SERIES.map(col => (
                      <td key={col.key}>
                        <Text
                          textAlign="right"
                          color={hiddenSeries?.has(col.series) ? 'neutral.300' : 'neutral.700'}
                        >
                          {formatCurrency(row[col.key] as number)}
                        </Text>
                      </td>
                    ))}
                  </ClickableTableRow>
                );
              })}
            </tbody>
          </table>
        </HtmlTable>
      </Card>
    </Box>
  );
};
